// =====================================================
// XML HELPERS — wrapped as ES module from src/js/xml-helpers.js
// Logic unchanged.
// =====================================================

export const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
const xmlSerializer = new XMLSerializer();

// All namespace prefixes that can appear in WordprocessingML fragments. parseFrag
// declares these on a wrapper so any prefixed fragment (w:, w14:, mc:, a:, …) parses
// even in isolation. Redundant declarations the serializer adds are stripped by
// serializeClean, so they never reach the output.
const FRAG_NS =
  ' xmlns:wpc="http://schemas.microsoft.com/office/word/2010/wordprocessingCanvas"' +
  ' xmlns:cx="http://schemas.microsoft.com/office/drawing/2014/chartex"' +
  ' xmlns:cx1="http://schemas.microsoft.com/office/drawing/2015/9/8/chartex"' +
  ' xmlns:cx2="http://schemas.microsoft.com/office/drawing/2015/10/21/chartex"' +
  ' xmlns:cx3="http://schemas.microsoft.com/office/drawing/2016/5/9/chartex"' +
  ' xmlns:cx4="http://schemas.microsoft.com/office/drawing/2016/5/10/chartex"' +
  ' xmlns:cx5="http://schemas.microsoft.com/office/drawing/2016/5/11/chartex"' +
  ' xmlns:cx6="http://schemas.microsoft.com/office/drawing/2016/5/12/chartex"' +
  ' xmlns:cx7="http://schemas.microsoft.com/office/drawing/2016/5/13/chartex"' +
  ' xmlns:cx8="http://schemas.microsoft.com/office/drawing/2016/5/14/chartex"' +
  ' xmlns:mc="http://schemas.openxmlformats.org/markup-compatibility/2006"' +
  ' xmlns:aink="http://schemas.microsoft.com/office/drawing/2016/ink"' +
  ' xmlns:am3d="http://schemas.microsoft.com/office/drawing/2017/model3d"' +
  ' xmlns:o="urn:schemas-microsoft-com:office:office"' +
  ' xmlns:oel="http://schemas.microsoft.com/office/2019/extlst"' +
  ' xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"' +
  ' xmlns:m="http://schemas.openxmlformats.org/officeDocument/2006/math"' +
  ' xmlns:v="urn:schemas-microsoft-com:vml"' +
  ' xmlns:wp14="http://schemas.microsoft.com/office/word/2010/wordprocessingDrawing"' +
  ' xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing"' +
  ' xmlns:w10="urn:schemas-microsoft-com:office:word"' +
  ' xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"' +
  ' xmlns:w14="http://schemas.microsoft.com/office/word/2010/wordml"' +
  ' xmlns:w15="http://schemas.microsoft.com/office/word/2012/wordml"' +
  ' xmlns:w16cex="http://schemas.microsoft.com/office/word/2018/wordml/cex"' +
  ' xmlns:w16cid="http://schemas.microsoft.com/office/word/2016/wordml/cid"' +
  ' xmlns:w16="http://schemas.microsoft.com/office/word/2018/wordml"' +
  ' xmlns:w16du="http://schemas.microsoft.com/office/word/2023/wordml/word16du"' +
  ' xmlns:w16sdtdh="http://schemas.microsoft.com/office/word/2020/wordml/sdtdatahash"' +
  ' xmlns:w16se="http://schemas.microsoft.com/office/word/2015/wordml/symex"' +
  ' xmlns:wpg="http://schemas.microsoft.com/office/word/2010/wordprocessingGroup"' +
  ' xmlns:wpi="http://schemas.microsoft.com/office/word/2010/wordprocessingInk"' +
  ' xmlns:wne="http://schemas.microsoft.com/office/word/2006/wordml"' +
  ' xmlns:wps="http://schemas.microsoft.com/office/word/2010/wordprocessingShape"' +
  ' xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture"' +
  ' xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"';

/**
 * Parse one or more WordprocessingML element fragments (e.g. "<w:p>…</w:p>" or
 * "<w:r>…</w:r>") into live DOM elements that can be manipulated as nodes. Throws
 * if the fragment isn't well-formed.
 */
export function parseFrag(xml: string): Element[] {
  const doc = new DOMParser().parseFromString(`<w:_frag${FRAG_NS}>${xml}</w:_frag>`, 'text/xml');
  const root = doc.documentElement;
  if (!root || root.getElementsByTagName('parsererror').length) throw new Error('parseFrag: malformed fragment');
  return Array.from(root.childNodes).filter(n => (n as Element).nodeType === 1) as Element[];
}

/** Parse a single element fragment and return its root element. */
export function parseEl(xml: string): Element {
  const els = parseFrag(xml);
  if (!els.length) throw new Error('parseEl: no element');
  return els[0];
}

/** Create a new WordprocessingML element (in the w: namespace by default). */
export function makeEl(doc: Document, qualifiedName: string, ns: string = W_NS): Element {
  return doc.createElementNS(ns, qualifiedName);
}

/**
 * Serialize a node, then strip the redundant xmlns declarations the serializer
 * adds to the fragment's outer element (they're already declared on the output
 * document root, so they're pure noise).
 */
export function serializeClean(node: Node): string {
  const s = xmlSerializer.serializeToString(node);
  const m = s.match(/^<([\w:.-]+)((?:\s+[\w:.-]+="[^"]*")*)(\s*\/?>)/);
  if (!m) return s;
  const cleanedAttrs = m[2].replace(/\s+xmlns(?::[\w.-]+)?="[^"]*"/g, '');
  return '<' + m[1] + cleanedAttrs + m[3] + s.slice(m[0].length);
}

export function serializeNode(node: Node): string {
  if (!node) return '';
  return xmlSerializer.serializeToString(node);
}

export function getInnerXml(element: Element): string {
  if (!element) return '';
  let inner = '';
  for (const child of Array.from(element.childNodes)) {
    inner += serializeNode(child);
  }
  return inner;
}

export function xmlEscape(str: string | null | undefined): string {
  if (str == null) return '';
  const s = String(str);
  if (!/[&<>"']/.test(s)) return s;
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

// ---------------------------------------------------------------------------
// Cross-document relationship tagging.
//
// Content copied from the ORIGINAL document (deleted paragraphs, moved-from
// blocks, merged footnote/endnote/comment definitions) carries r:id / r:embed /
// r:link attributes that are only meaningful against the ORIGINAL part's .rels.
// Emitted into the output (whose rels come from the REVISED doc) they either
// dangle (Word "unreadable content" repair) or collide with an unrelated rev
// relationship (a hyperlink silently pointing at the WRONG target). Every emit
// site for orig-sourced XML tags the ids with an FRO- prefix; a post-assembly
// pass in the generator resolves each tag by importing the relationship (and
// any internal target part) from the original, or strips the construct when it
// cannot be resolved. header/footerReference ids are tagged FRH- instead: they are resolved by
// what the parts say (headers-footers.ts resolveSectionRefs).
// ---------------------------------------------------------------------------
export function tagCrossRelIds(xml: string): string {
  if (!xml || (xml.indexOf('r:') === -1 && xml.indexOf('o:relid') === -1 && xml.indexOf('noteReference') === -1 && xml.indexOf('<w:comment') === -1 && xml.indexOf('<w:bookmark') === -1)) return xml;
  const saved: string[] = [];
  let x = xml.replace(/<w:(?:header|footer)Reference\b[^>]*\/?>/g,
    (m) => { saved.push(m); return `\uE000${saved.length - 1}\uE001`; });
  x = x.replace(/\b(r:(?:id|embed|link|dm|lo|qs|cs)|o:relid)="(?!FRO-)([^"]+)"/g, '$1="FRO-$2"');
  // A footnote or endnote mark's id names the original's note, which the revised draft may number
  // differently: tagged the same way, resolved with the notes (notes.ts resolveNoteRefs).
  x = x.replace(/(<w:(?:footnote|endnote)Reference\b[^>]*?\bw:id=")(\d+)"/g, '$1FRO-$2"');
  // A comment mark's id names the original's comment, which the revised draft may number for
  // another: tagged FRC-, resolved with the comments (comments-part.ts resolveComments).
  x = x.replace(/(<w:comment(?:RangeStart|RangeEnd|Reference)\b[^>]*?\bw:id=")(\d+)"/g, '$1FRC-$2"');
  // A bookmark is the original's: tagged FRB-, so it never pairs with the revised draft's of the
  // same number, and is left out where the revised draft has one of its name (bookmarks.ts).
  x = x.replace(/(<w:bookmark(?:Start|End)\b[^>]*?\bw:id=")(-?\d+)"/g, '$1FRB-$2"');
  // A section break's header and footer references: their ids are the original's relationships,
  // resolved by what the parts say (headers-footers.ts resolveSectionRefs).
  if (saved.length) x = x.replace(/\uE000(\d+)\uE001/g, (_m, i) => saved[+i].replace(/\br:id="(?!FRH-)([^"]+)"/, 'r:id="FRH-$1"'));
  return x;
}
